import { useState, useEffect, useCallback, useRef } from 'react';
import api from 'services/api';
import { useTenant } from 'context/TenantContext';
import { exportPagePdf } from 'utils/exportPdf';

const MOIS_NOMS = ['Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];

const fmt = (val) => Number(val || 0).toLocaleString() + ' FCFA';

export default function CompteResultat() {
  // IDENTITE TENANT — ce document est REMIS AU CLIENT ou au salarie. Il portait
  // « GLADIATOR SECURITY SYSTEMS » EN DUR : l'identite de l'EDITEUR imprimee sur
  // le document d'un tenant. Meme famille que les 4 fuites corrigees en juillet-aout
  // sur les PDF backend, mais cote frontend — non couverte par la matrice du garde-fou.
  const tenant = useTenant();

  const now = new Date();
  const [periode, setPeriode] = useState('mois');
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [evolution, setEvolution] = useState([]);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resData, resEvo] = await Promise.all([
        api.get(`/compte-resultat?mois=${mois}&annee=${annee}&periode=${periode}`),
        api.get(`/compte-resultat/evolution?annee=${annee}`)
      ]);
      setData(resData.data);
      setEvolution(resEvo.data);
    } catch (err) {
      console.error('Erreur chargement compte de resultat', err);
    } finally {
      setLoading(false);
    }
  }, [mois, annee, periode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const produits = data?.produits || {};
  const charges = data?.charges || {};

  const totalProduits = Number(produits.facturation_gardiennage || 0)
    + Number(produits.facturation_telesurveillance || 0)
    + Number(produits.ventes_equipements || 0)
    + Number(produits.autres_produits || 0);

  // ⚠️ DEFAUT CONNU, LAISSE EN L'ETAT SCIEMMENT — decision de Dom, 11/09/2026.
  //    Cette somme lit `charges.loyer`, `charges.carburant` et
  //    `charges.achats_materiel` : TROIS CHAMPS QUE LE SERVEUR NE RENVOIE PAS,
  //    donc trois zeros. Et `Number(charges.autres_charges)` porte sur un
  //    TABLEAU : vide il vaut 0, NON VIDE il vaut NaN. `gladiator_sec.charges`
  //    etant vide en production, le defaut est ARME ET JAMAIS DECLENCHE — il
  //    le sera a la premiere charge saisie.
  //    Le serveur rend deja `charges.total`, juste et complet : deux totaux
  //    pour une notion.
  //    ➡️ NE PAS LE CORRIGER ICI. Il appartient au chantier « chaine
  //       comptabilite » (ops/chantier-chaine-comptabilite.md), avec ses deux
  //       freres. Le corriger seul traiterait un symptome et laisserait la
  //       cause : TROIS CHEMINS calculent la meme chose.
  //    ⓘ Ce qui A change dans ce lot : les cles renommees, et le retrait de la
  //      part SALARIALE de cette somme — elle est prelevee SUR le brut, donc
  //      l'additionner la comptait deux fois.
  const totalCharges = Number(charges.masse_salariale_brute || 0)
    + Number(charges.charges_patronales || 0)
    + Number(charges.loyer || 0)
    + Number(charges.carburant || 0)
    + Number(charges.achats_materiel || 0)
    + Number(charges.autres_charges || 0);

  const resultatNet = totalProduits - totalCharges;

  const periodeLabel = periode === 'mois'
    ? `${MOIS_NOMS[mois - 1]} ${annee}`
    : periode === 'trimestre'
      ? `T${Math.ceil(mois / 3)} ${annee}`
      : `Annee ${annee}`;

  const prevCA = data?.prev_ca ?? null;
  const pctChange = prevCA && prevCA > 0
    ? (((totalProduits - prevCA) / prevCA) * 100).toFixed(1)
    : null;

  const maxEvo = evolution.reduce((m, e) => Math.max(m, Number(e.ca || 0), Number(e.charges || 0)), 1);

  const years = [];
  for (let y = 2024; y <= annee + 1; y++) years.push(y);

  return (
    <div>
{/* TOOLBAR */}
      <div className="gss-toolbar" style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <select className="gss-input" value={periode} onChange={e => setPeriode(e.target.value)} style={{ width: 'auto' }}>
          <option value="mois">Mois</option>
          <option value="trimestre">Trimestre</option>
          <option value="annee">Annee</option>
        </select>
        {periode !== 'annee' && (
          <select className="gss-input" value={mois} onChange={e => setMois(Number(e.target.value))} style={{ width: 'auto' }}>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>{MOIS_NOMS[i]}</option>
            ))}
          </select>
        )}
        <select className="gss-input" value={annee} onChange={e => setAnnee(Number(e.target.value))} style={{ width: 'auto' }}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button className="gss-btn gss-btn-primary" onClick={() => exportPagePdf(`Compte de résultat — ${periodeLabel}`, printRef.current, tenant)}>Exporter PDF</button>
      </div>

      {loading ? (
        <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
      ) : (
        <div ref={printRef}>
          {/* COMPANY HEADER */}
          <div style={{
            textAlign: 'center', padding: '1.5rem', margin: '1rem 0',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            borderRadius: 8, color: '#fff'
          }}>
            {/* Identite du TENANT, jamais de l'editeur. Le slogan « Integrateur de
                systemes de securite » etait celui de GLADIATOR : il n'a aucun sens
                pour un autre tenant et disparait. RCCM affiche seulement s'il existe. */}
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 1 }}>{tenant?.nom || ''}</div>
            {tenant?.rccm && (
              <div style={{ fontSize: 13, color: '#aaa', marginTop: 4 }}>RCCM {tenant.rccm}</div>
            )}
            <div style={{ fontSize: 15, marginTop: 8, color: '#e0e0e0' }}>Compte de résultat — {periodeLabel}</div>
          </div>

          {/* PRODUITS */}
          <div className="content-card">
            <div className="content-card-title">Produits</div>
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead>
                  <tr><th>Libelle</th><th style={{ textAlign: 'right' }}>Montant</th></tr>
                </thead>
                <tbody>
                  <tr><td>Facturation gardiennage</td><td style={{ textAlign: 'right' }}>{fmt(produits.facturation_gardiennage)}</td></tr>
                  <tr><td>Facturation telesurveillance</td><td style={{ textAlign: 'right' }}>{fmt(produits.facturation_telesurveillance)}</td></tr>
                  <tr><td>Ventes equipements</td><td style={{ textAlign: 'right' }}>{fmt(produits.ventes_equipements)}</td></tr>
                  <tr><td>Autres produits</td><td style={{ textAlign: 'right' }}>{fmt(produits.autres_produits)}</td></tr>
                  <tr style={{ background: 'rgba(46,125,50,0.12)', fontWeight: 700 }}>
                    <td>TOTAL PRODUITS</td>
                    <td style={{ textAlign: 'right', color: '#2e7d32' }}>{fmt(totalProduits)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* CHARGES */}
          <div className="content-card">
            <div className="content-card-title">Charges</div>
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead>
                  <tr><th>Libelle</th><th style={{ textAlign: 'right' }}>Montant</th></tr>
                </thead>
                <tbody>
                  <tr><td>Masse salariale brute<div style={{ fontSize: 11, color: '#92400E' }}>Périmètre : agents de sécurité uniquement, bulletins validés ou payés. Le personnel administratif n'est pas inclus dans ce total.</div></td><td style={{ textAlign: 'right' }}>{fmt(charges.masse_salariale_brute)}</td></tr>
                  {/* ⚠️ AUCUN TAUX DANS LE LIBELLÉ. Il en portait un — « 13% » — alors que la
                      colonne lue en calculait 16,5 et que le référentiel en dit 22,5 :
                      trois valeurs pour une notion, dont une écrite en dur dans un libellé
                      d'écran. Le taux vit au référentiel, daté et sourcé ; l'écran affiche
                      un MONTANT. */}
                  <tr><td>Charges patronales<div style={{ fontSize: 11, color: '#92400E' }}>CNSS, INAM et taxes sur salaires, aux taux du référentiel du pays à la date du bulletin.</div></td><td style={{ textAlign: 'right' }}>{fmt(charges.charges_patronales)}</td></tr>
                  {/* ⚠️ LIGNE INFORMATIVE, HORS TOTAL : la part salariale est prélevée SUR le
                      brut déjà compté au-dessus. L'additionner la compterait deux fois. */}
                  <tr style={{ opacity: 0.75 }}><td>Part salariale retenue <em>(pour mémoire — comprise dans la masse salariale brute)</em></td><td style={{ textAlign: 'right' }}>{fmt(charges.charges_salariales)}</td></tr>
                  <tr><td>Loyer et charges</td><td style={{ textAlign: 'right' }}>{fmt(charges.loyer)}</td></tr>
                  <tr><td>Carburant</td><td style={{ textAlign: 'right' }}>{fmt(charges.carburant)}</td></tr>
                  <tr><td>Achats materiel</td><td style={{ textAlign: 'right' }}>{fmt(charges.achats_materiel)}</td></tr>
                  <tr><td>Autres charges</td><td style={{ textAlign: 'right' }}>{fmt(charges.autres_charges)}</td></tr>
                  <tr style={{ background: 'rgba(229,57,53,0.12)', fontWeight: 700 }}>
                    <td>TOTAL CHARGES</td>
                    <td style={{ textAlign: 'right', color: '#e53935' }}>{fmt(totalCharges)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* RESULTAT NET */}
          <div style={{
            textAlign: 'center', padding: '1.5rem', margin: '1rem 0',
            background: resultatNet >= 0 ? 'rgba(46,125,50,0.1)' : 'rgba(229,57,53,0.1)',
            border: `2px solid ${resultatNet >= 0 ? '#2e7d32' : '#e53935'}`,
            borderRadius: 8
          }}>
            <div style={{ fontSize: 14, color: '#888', marginBottom: 4 }}>RESULTAT NET</div>
            <div style={{
              fontSize: 28, fontWeight: 700,
              color: resultatNet >= 0 ? '#2e7d32' : '#e53935'
            }}>
              {fmt(resultatNet)}
            </div>
            <div style={{ fontSize: 13, color: '#999', marginTop: 4 }}>
              {resultatNet >= 0 ? 'Benefice' : 'Deficit'} - {periodeLabel}
            </div>
          </div>

          {/* EVOLUTION CHART */}
          <div className="content-card">
            <div className="content-card-title">Evolution sur 12 mois</div>
            <div style={{ padding: '0.5rem 0' }}>
              {evolution.map((e, i) => {
                const ca = Number(e.ca || 0);
                const ch = Number(e.charges || 0);
                const caPct = (ca / maxEvo) * 100;
                const chPct = (ch / maxEvo) * 100;
                return (
                  <div key={i} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#ccc' }}>
                      {MOIS_NOMS[Number(e.mois || i + 1) - 1] || `Mois ${i + 1}`}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{ width: 70, fontSize: 11, color: '#888', textAlign: 'right' }}>CA</span>
                      <div style={{ flex: 1, background: '#1a1a2e', borderRadius: 4, height: 18, overflow: 'hidden' }}>
                        <div style={{
                          width: `${caPct}%`, height: '100%',
                          background: 'linear-gradient(90deg, #2e7d32, #43a047)',
                          borderRadius: 4, minWidth: ca > 0 ? 2 : 0,
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                      <span style={{ width: 120, fontSize: 11, color: '#aaa', textAlign: 'right' }}>{fmt(ca)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 70, fontSize: 11, color: '#888', textAlign: 'right' }}>Charges</span>
                      <div style={{ flex: 1, background: '#1a1a2e', borderRadius: 4, height: 18, overflow: 'hidden' }}>
                        <div style={{
                          width: `${chPct}%`, height: '100%',
                          background: 'linear-gradient(90deg, #c62828, #e53935)',
                          borderRadius: 4, minWidth: ch > 0 ? 2 : 0,
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                      <span style={{ width: 120, fontSize: 11, color: '#aaa', textAlign: 'right' }}>{fmt(ch)}</span>
                    </div>
                  </div>
                );
              })}
              {evolution.length === 0 && (
                <p style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Aucune donnee d'evolution disponible</p>
              )}
            </div>
          </div>

          {/* STATS CARDS */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-card-header"><span className="stat-card-label">CA du mois</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg></div></div>
              <div className="stat-card-value">{Number(totalProduits).toLocaleString()}</div>
              <div className="stat-card-sub">
                {pctChange !== null ? (
                  <span style={{ color: Number(pctChange) >= 0 ? '#2e7d32' : '#e53935' }}>
                    {Number(pctChange) >= 0 ? '+' : ''}{pctChange}% vs mois precedent
                  </span>
                ) : 'FCFA'}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><span className="stat-card-label">Charges du mois</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg></div></div>
              <div className="stat-card-value" style={{ color: '#e53935' }}>{Number(totalCharges).toLocaleString()}</div>
              <div className="stat-card-sub">FCFA</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><span className="stat-card-label">Resultat net</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="22" height="18" rx="2"/><line x1="1" y1="9" x2="23" y2="9"/><line x1="1" y1="15" x2="23" y2="15"/><line x1="12" y1="3" x2="12" y2="21"/></svg></div></div>
              <div className="stat-card-value" style={{ color: resultatNet >= 0 ? '#2e7d32' : '#e53935' }}>
                {Number(resultatNet).toLocaleString()}
              </div>
              <div className="stat-card-sub">FCFA</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><span className="stat-card-label">Impayes clients</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg></div></div>
              <div className="stat-card-value" style={{ color: 'var(--gss-warning)' }}>
                {Number(data?.impayes || 0).toLocaleString()}
              </div>
              <div className="stat-card-sub">FCFA</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
